import { FavoriteItem, User } from "../../AuthProvider";
import { conf } from "../../Configuration/conf";
import { displayCheck } from "../../Configuration/SectionsMenu";
import { isRequireFlagAccess } from "../../Configuration/utils";
import { getCurrentLocale, Locale } from "../../Locale";
import {
  InternalRedirection,
  Option,
  SubMenuName,
  UrlsLanguagesType,
} from "../interface";
import {
  ADMIN_REVBELL_SUBMENU_HIERARCHY,
  ADMIN_SUBMENU_HIERARCHY,
  DEFAULT_SUBMENU_HIERARCHY,
  MARKETPLACE_SUBMENU_HIERARCHY,
  PILOTAGE_OPTIMISATION_SUBMENU_HIERARCHY,
  REPORTING_SUBMENU_HIERARCHY,
  SUPPORT_SUBMENU_HIERARCHY,
} from "./menusStructure";

/**
 * Hardcoded React routes, not driven by the API menu, keyed by the module route
 * they follow: each is accessible exactly when that route is (favorites
 * visibility and submenu accessibility checks).
 */
const HARDCODED_ROUTES: Record<string, string> = {
  "/cockpit/": "/alerte/hebergements/",
};

/**
 * Pages to exclude from the menu
 */
export const menuToExclude = [
  "alerte",
  "active_alerts",
  "mass_pricing",
  "pricing",
];

/**
 * Pages to implement in admin menu (therefore they are excluded from the menu)
 */
export const adminModules = [
  "activity",
  "administration",
  "connectivity",
  "customers",
  "learning_models",
  "modules",
  "settings",
  "users",
];

/**
 * Pages to implement in reporting menu
 */
export const reportingModules = ["events", "cockpit"];

/**
 * Transform configuration json to adapt it to subMenu format
 */
export const formatConfigurationJson = (
  currentUser: User | null,
  selectedCustomer: string | null = null
) => {
  const confMenu: Option[] = conf.sections.map((section) => {
    const enablePage = section.pages.filter((page) =>
      displayCheck(page, currentUser, selectedCustomer)
    );

    return {
      type: "internalRedirection",
      value: section.label,
      menuLevel: 0,
      toTranslate: true,
      nestedOptions: enablePage
        .map((page) => {
          if (page.hideInMenu) return null;

          const flagDisabled = !isRequireFlagAccess(page, currentUser);

          return {
            type: "internalRedirection" as const,
            value: page.label,
            menuLevel: 1,
            redirectionTo: flagDisabled
              ? "#"
              : `configuration/${section.path}/${page.path}`,
            toTranslate: true,
            isDisable: flagDisabled,
            name: page.name,
          };
        })
        .filter((el) => !!el),
    };
  });

  return confMenu;
};

/**
 * Check if a route is accessible, either directly or as a sub-page of an accessible module
 */
const normalizeRoute = (value: string) =>
  value.endsWith("/") && value.length > 1 ? value.slice(0, -1) : value;

// Routes flagged isNew in the submenu templates, so dynamic entries
// (favorites) pointing to them show the same "New" chip.
const extractNewRoutes = (options: Option[]): string[] =>
  options.flatMap((opt) => [
    ...(opt.type === "internalRedirection" && opt.isNew && opt.redirectionTo
      ? [normalizeRoute(opt.redirectionTo)]
      : []),
    ...extractNewRoutes(opt.nestedOptions ?? []),
  ]);

const NEW_ROUTES = new Set(
  extractNewRoutes(PILOTAGE_OPTIMISATION_SUBMENU_HIERARCHY)
);

const isNewRoute = (route: string): boolean =>
  NEW_ROUTES.has(normalizeRoute(route));

export const isRouteAccessible = (
  route: string,
  accessibleRoutes: Set<string>
): boolean => {
  const normalizedRoute = normalizeRoute(route);

  return [...accessibleRoutes].some((rawAccessibleRoute) => {
    const accessibleRoute = normalizeRoute(rawAccessibleRoute);
    return (
      normalizedRoute === accessibleRoute ||
      normalizedRoute.startsWith(`${accessibleRoute}/`)
    );
  });
};

export const withHardcodedRoutes = (routes: Set<string>): Set<string> =>
  new Set([
    ...routes,
    ...Object.entries(HARDCODED_ROUTES)
      .filter(([, moduleRoute]) => isRouteAccessible(moduleRoute, routes))
      .map(([route]) => route),
  ]);

const menuBuilder = (
  structure: InternalRedirection[],
  activeMenu: InternalRedirection[],
  isStaff: boolean,
  accessibleRoutes: Set<string> = new Set(),
  // Set useMock to true if you want use json menu data instead of api menu data
  useMock?: boolean
) => {
  return structure.map((section) => ({
    ...section,
    nestedOptions: (section.nestedOptions || [])
      .map((menu) => {
        // Just to satisfy Typescript, because we need to use "redirectionTo"
        if (menu.type !== "internalRedirection") return null;

        if (menu.isStaff && !isStaff) return null;

        if (menu.forceAddToMenu) {
          if (
            menu.redirectionTo &&
            accessibleRoutes.size > 0 &&
            !isRouteAccessible(menu.redirectionTo, accessibleRoutes)
          ) {
            return null;
          }
          return { ...menu };
        }

        const activeModule = activeMenu.find(
          (item) => menu.redirectionTo === item.redirectionTo
        );

        return activeModule
          ? useMock
            ? { ...menu }
            : menu.useTranslationKey
            ? {
                ...activeModule,
                value: menu.value,
                toTranslate: menu.toTranslate,
              }
            : { ...activeModule }
          : null;
      })
      .filter((item) => !!item),
  }));
};

/**
 * Format pilotage & optimisation submenu applying feature flag checks
 * and filtering items whose module route is not accessible
 */
export const formatPilotageMenu = (
  currentUser: User | null,
  accessibleRoutes: Set<string> = new Set()
): InternalRedirection[] => {
  const isStaff = !!currentUser?.is_staff;
  return PILOTAGE_OPTIMISATION_SUBMENU_HIERARCHY.map((section) => ({
    ...section,
    nestedOptions: (section.nestedOptions || [])
      .map((item): Option | null => {
        if (item.type !== "internalRedirection") return item;

        if (item.isStaff && !isStaff) return null;

        if (
          item.redirectionTo &&
          accessibleRoutes.size > 0 &&
          !isRouteAccessible(item.redirectionTo, accessibleRoutes)
        ) {
          return null;
        }

        if (!item.flagRequired) return item;

        const flagActive =
          currentUser?.tenant?.flags[
            item.flagRequired as keyof typeof currentUser.tenant.flags
          ] === "1";

        return flagActive
          ? item
          : { ...item, isDisable: true, redirectionTo: "#" };
      })
      .filter((item): item is Option => item !== null),
  }));
};

/**
 * Format administration json with menu list to adapt it to subMenu
 */
export const formatAdministrationMenu = (
  adminMenus: InternalRedirection[],
  isStaff: boolean,
  accessibleRoutes: Set<string> = new Set()
): InternalRedirection[] => {
  return menuBuilder(
    ADMIN_SUBMENU_HIERARCHY,
    adminMenus,
    isStaff,
    accessibleRoutes
  );
};

export const formatReportingMenu = (
  reportingMenus: InternalRedirection[],
  isStaff: boolean,
  accessibleRoutes: Set<string> = new Set()
): InternalRedirection[] => {
  return menuBuilder(
    REPORTING_SUBMENU_HIERARCHY,
    reportingMenus,
    isStaff,
    accessibleRoutes,
    true
  );
};

/**
 * For Each submenu get options to display
 *
 * For admin settings submenu, we check if current menu is enable in menu list api before displaying it
 * @param activeItem
 * @param adminMenus
 */
export const getSubMenuOptions = (
  activeItem: {
    name: string;
    translatedName?: string;
  },
  adminMenus: InternalRedirection[],
  reportingMenus: InternalRedirection[],
  currentUser: User | null,
  isStaff: boolean,
  favorites: FavoriteItem[] = [],
  accessibleRoutes: Set<string> = new Set(),
  selectedCustomer: string | null = null
): Option[] => {
  if (activeItem.name === SubMenuName.FAVORITES) {
    const visibleFavorites =
      accessibleRoutes.size > 0
        ? favorites.filter((fav) => isRouteAccessible(fav.id, accessibleRoutes))
        : favorites;
    return [
      {
        type: "internalRedirection",
        value: "FAVORITES_MENU",
        menuLevel: null,
        toTranslate: false,
        nestedOptions: visibleFavorites.map((fav) => ({
          type: "internalRedirection" as const,
          value: fav.label,
          redirectionTo: fav.id,
          menuLevel: 1,
          toTranslate: false,
          isNew: isNewRoute(fav.id),
        })),
      },
    ];
  }

  if (activeItem.name === SubMenuName.MARKETPLACE) {
    return MARKETPLACE_SUBMENU_HIERARCHY;
  } else if (activeItem.name === SubMenuName.CONFIGURATION) {
    return formatConfigurationJson(currentUser, selectedCustomer);
  } else if (activeItem.name === SubMenuName.ADMINISTRATION_SETTINGS) {
    return formatAdministrationMenu(adminMenus, isStaff, accessibleRoutes);
  } else if (activeItem.name === SubMenuName.SUPPORT) {
    return SUPPORT_SUBMENU_HIERARCHY;
  } else if (activeItem.name === SubMenuName.REPORTING) {
    return formatReportingMenu(reportingMenus, isStaff, accessibleRoutes);
  } else if (activeItem.name === SubMenuName.ADMIN_REVBELL) {
    return ADMIN_REVBELL_SUBMENU_HIERARCHY;
  } else if (activeItem.name === SubMenuName.PILOTAGE_OPTIMISATION) {
    return formatPilotageMenu(currentUser, accessibleRoutes);
  }

  return DEFAULT_SUBMENU_HIERARCHY;
};

/**
 * Select url to use according locale
 */
export const getUrl = (locale: Locale, option: UrlsLanguagesType): string => {
  if (locale === Locale.FR_FR && option.urlFR) {
    return option.urlFR;
  }
  return option.urlAll;
};

/**
 * Get first url in admin menus to add it on click on "Administration" menu
 */
export const getFirstAdminMenuPage = (
  adminMenus: InternalRedirection[],
  isStaff: boolean
): string | undefined => {
  const adminMenusFormatted = formatAdministrationMenu(adminMenus, isStaff);
  const flatMapAdminMenus = adminMenusFormatted.flatMap((menu) =>
    menu.nestedOptions ? menu.nestedOptions : []
  );

  if (flatMapAdminMenus.length > 0) {
    const firstMenu = flatMapAdminMenus[0];
    if (firstMenu?.type === "internalRedirection") {
      return firstMenu.redirectionTo;
    } else if (firstMenu?.type === "externalRedirection") {
      const locale = getCurrentLocale();
      return getUrl(locale, firstMenu);
    }
  }

  return undefined;
};

/**
 * Recursively extract all redirectionTo routes from a list of Options
 */
export const extractRoutes = (options: Option[]): string[] =>
  options.flatMap((opt) => [
    ...(opt.type === "internalRedirection" &&
    opt.redirectionTo &&
    opt.redirectionTo !== "#"
      ? [opt.redirectionTo]
      : []),
    ...extractRoutes(opt.nestedOptions ?? []),
  ]);

/**
 * Get all menus admin that are configuration pages
 */
export const getConfigurationPagesFromAdminMenus =
  (): InternalRedirection[] => {
    const flatAdminMenus = ADMIN_SUBMENU_HIERARCHY.flatMap((menu) =>
      menu.nestedOptions ? (menu.nestedOptions as InternalRedirection[]) : []
    );

    return flatAdminMenus.filter((menu) => menu.configMenu);
  };

/**
 * Generates the initials of a full name
 */
export function stringAvatar(name: string) {
  const splitedName = name.split(" ").filter(Boolean);

  const firstLetter = splitedName[0]?.[0] ?? "";
  const secondLetter = splitedName[1]?.[0] ?? "";

  return {
    children: firstLetter + secondLetter,
  };
}
